import axios from 'axios';
import { debounce } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';

/** Бүс шалгах хүсэлтийн хоцролт — хаяг бичих үед товч дарах бүрд дуудахгүй */
const DEBOUNCE_DELAY = 600;

/**
 * `out-off-zone` нь public эндпойнт тул global axios instance-ийг хэрэглэхгүй.
 * App.js-ийн interceptor хүсэлт бүр дээр бүтэн хуудсын loader-ийг асааж, Authorization
 * header залгадаг — хаяг сонгох үед loader анивчихгүй байх, public API-д merchant token
 * явуулахгүй байхын тулд тусад нь instance үүсгэнэ.
 */
const publicClient = axios.create();

/**
 * Бүс шалгах эндпойнтын суурь хаяг.
 *
 * `REACT_APP_ZONE_URL` тохируулсан бол түүнийг шууд авна — локал ppadmin руу зөвхөн бүсийн
 * шалгалтыг чиглүүлэхэд хэрэгтэй (`REACT_APP_ADDRESS_URL_V2`-ыг шилжүүлбэл хаягийн
 * жагсаалт, газрын зургийн хайлт хоёр ч локал руу орж, тэд MEZORN key шаарддаг тул унана).
 *
 * Үгүй бол `.../public/v1.0/addressing`-аас `.../public/v1.0/address` болгож гаргана.
 * Хоёулаа байхгүй бол `null` — relative URL-ээр merchant web хост руу хүсэлт явуулахгүй.
 */
const getAddressBaseUrl = () => {
  const zoneUrl = process.env.REACT_APP_ZONE_URL;
  if (zoneUrl) return zoneUrl.replace(/\/$/, '');

  const addressingUrl = process.env.REACT_APP_ADDRESS_URL_V2;
  if (!addressingUrl) return null;

  return addressingUrl.replace(/\/addressing$/, '/address');
};

/**
 * Хаяг хүргэлтийн бүсэд байгаа эсэхийг шалгана.
 *
 * @param query `buildZoneQuery`-ийн үр дүн — `{ lat, lon }` эсвэл
 *              `{ addressText, regionId }`. `null` бол дуудахгүй.
 * @returns `{ loading, data, error }` — `data` нь `{ isOutOfZone, zoneName, distanceKm }`.
 */
const useOutOfZone = (query) => {
  const [state, setState] = useState({ loading: false, data: null, error: null });

  // Зөвхөн хамгийн сүүлийн хүсэлт state-д бичнэ — хоцорсон хариу шинийг дарж бичихгүй
  const requestId = useRef(0);

  // Объектын identity render бүрд шинэ болдог тул effect-ийн dependency-д текст хэлбэрээр авна
  const queryKey = query ? JSON.stringify(query) : '';

  const fetchZone = useMemo(
    () =>
      debounce(async (params, id) => {
        const baseUrl = getAddressBaseUrl();

        if (!baseUrl) {
          console.warn('out-off-zone: REACT_APP_ADDRESS_URL_V2 тохируулаагүй тул шалгалт алгасав');
          if (id === requestId.current) setState({ loading: false, data: null, error: null });
          return;
        }

        try {
          const { data } = await publicClient.get(`${baseUrl}/out-off-zone`, {
            params,
          });

          if (id !== requestId.current) return;
          setState({ loading: false, data: data?.result ?? null, error: null });
        } catch (error) {
          if (id !== requestId.current) return;
          // Бүсийн шалгалт нь зөвхөн мэдээлэл — алдаа гарвал захиалгын урсгалыг тасалдуулахгүй
          console.error('out-off-zone error: ', error);
          setState({ loading: false, data: null, error });
        }
      }, DEBOUNCE_DELAY),
    []
  );

  useEffect(() => {
    requestId.current += 1;

    if (!queryKey) {
      fetchZone.cancel();
      setState({ loading: false, data: null, error: null });
      return;
    }

    setState((prev) => ({ ...prev, loading: true }));
    fetchZone(JSON.parse(queryKey), requestId.current);
  }, [queryKey, fetchZone]);

  // Компонент саллаа ч хүлээгдэж байгаа дуудалт хожим ажиллахгүй
  useEffect(() => () => fetchZone.cancel(), [fetchZone]);

  return state;
};

export default useOutOfZone;
